import { ChangeDetectorRef, Component, OnInit, ViewChild, AfterViewInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  CAMERA_ONLINE_STATUS_FILTER_TYPES,
  DAYS_INACTIVE_OPTIONS,
  HEALTH_CHECK_TABLE_PAGE_SIZE,
  LIVE_DEVICE_LIST_TABLE,
} from '@app-assets/constants/assets.constants';
import { GetDevicePingListResponseRow } from '@app-assets/models/assets.model';
import { AssetsService } from '@app-assets/services/assets.service';
import { DataService } from '@app-core/services/data/data.service';
import { AssetAutocompleteComponent } from '@app-shared/components/asset-autocomplete/asset-autocomplete.component';
import { Subject } from 'rxjs';
import { finalize, skip, takeUntil } from 'rxjs/operators';
import { CamerOnlineStatusParams } from '@app-assets/models/assets.model';
import { AssetAutoCompleteResponseObject, RequestDownloadReportBody } from '@app-core/models/core.model';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { TranslateService } from '@ngx-translate/core';
import { AsyncReportType, ScheduledReportType } from '@app-core/constants/constants';
import { Location } from '@angular/common';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

interface CameraOnlineStatusDownloadReportBody extends RequestDownloadReportBody {
  sort?: string;
  sortBy?: string;
  daysInactive?: string;
  assetId?: string;
  deviceId?: string;
  fleetPortal: boolean;
}

@Component({
  selector: 'app-camera-online-status',
  templateUrl: './camera-online-status.component.html',
  styleUrls: ['./camera-online-status.component.scss'],
})
export class CameraOnlineStatusComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('paginator') public paginator: MatPaginator;
  @ViewChild(AssetAutocompleteComponent, { static: false }) assetAutocomplete: AssetAutocompleteComponent;
  @ViewChild('sort', { static: true }) public sort: MatSort;

  @Input() currentsubtabSelected: string = '';
  public liveDevicesListDataSource: MatTableDataSource<GetDevicePingListResponseRow> = new MatTableDataSource([]);
  public assetFilterTypeList = CAMERA_ONLINE_STATUS_FILTER_TYPES().sort((a, b) => (a.label > b.label ? 1 : -1));
  public toggleAdvancedSearch: boolean = false;
  public assetFilterForm: FormGroup;
  public loader: boolean = true;
  public pageSize: number = HEALTH_CHECK_TABLE_PAGE_SIZE;
  public isSearchButtonDisabled: boolean = true;
  public liveDevicesListTableColumns: string[] = LIVE_DEVICE_LIST_TABLE;
  public csvDownloadLoader = false;
  public csvDownloadSuccess: boolean = false;
  private searchedAssetId: string = '';
  private searchedDeviceId: string = '';
  private searchedDaysInactiveRange: string = '';

  public daysInactiveFilterOptions: { label: string; value: string }[] = DAYS_INACTIVE_OPTIONS;

  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private offset: number = 0;
  public cameraOnlineStatustotalCount: number = 0;

  constructor(
    public dataService: DataService,
    private assetService: AssetsService,
    private cdRef: ChangeDetectorRef,
    private snackBarService: SnackBarService,
    private asyncDownloadsService: AsyncDownloadsService,
    private location: Location,
    public translate: TranslateService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    this.assetFilterForm = new FormGroup({
      filterType: new FormControl('assetIdFilter'),
      deviceId: new FormControl(''),
      asset: new FormControl(),
      daysInactive: new FormControl(''),
    });

    this.assetFilterForm
      .get('deviceId')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.updateSearchButtonDisabled();
      });
    this.assetFilterForm
      .get('daysInactive')
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.updateSearchButtonDisabled();
      });
    this.liveDevicesListDataSource.paginator = this.paginator;
    this.getcameraOnlineList();

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.navigateBack();
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public navigateBack() {
    this.location.back();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.toggleAdvancedSearch) {
      this.toggleAdvancedSearch = false;
      this.clearSearchForm();
      this.getcameraOnlineList(true);
    }
    if (changes && changes.currentsubtabSelected && this.paginator) {
      this.paginator.pageIndex = 0;
      this.offset = 0;
      if (this.searchedAssetId || this.searchedDeviceId || this.searchedDaysInactiveRange) {
        this.searchedAssetId = null;
        this.searchedDeviceId = null;
        this.searchedDaysInactiveRange = null;
      }
      this.getcameraOnlineList(true);
    }
  }

  public ngAfterViewInit(): void {
    this.liveDevicesListDataSource.sort = this.sort;

    this.paginator.page.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event: PageEvent) => {
      this.offset = event.pageIndex * this.pageSize;
      this.getcameraOnlineList();
    });

    this.sort?.sortChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.paginator.pageIndex = 0;
      this.offset = 0;
      this.getcameraOnlineList(true);
    });
  }

  public clearInput(input: string) {
    this.assetFilterForm.patchValue({
      [input]: '',
    });
  }

  public clearSearchForm() {
    const { filterType = 'assetIdFilter' } = this.assetFilterForm.value || {};
    this.assetFilterForm.reset();
    this.assetFilterForm.patchValue({
      filterType: 'assetIdFilter',
    });
    (this.searchedAssetId = null), (this.searchedDeviceId = null);
    this.searchedDaysInactiveRange = null;
    if (filterType === 'assetIdFilter') {
      this.clearAssetId();
    } else if (filterType === 'deviceIdFilter') {
      this.clearDeviceId();
    } else {
      this.clearDaysInactive();
    }
    this.getcameraOnlineList(true);
  }

  public toggleAdvancedFilters() {
    this.toggleAdvancedSearch = !this.toggleAdvancedSearch;
    if (!this.toggleAdvancedSearch) {
      this.clearSearchForm();
      this.offset = 0;
      this.paginator.firstPage();
      this.getcameraOnlineList(true);
    }
    this.cdRef.detectChanges();
  }

  public onSearch() {
    this.paginator.firstPage();
    const { filterType = 'assetIdFilter', asset, deviceId, daysInactive } = this.assetFilterForm.value || {};
    if (filterType === 'assetIdFilter') {
      this.searchByAssetId(asset?.assetId);
    } else if (filterType === 'deviceIdFilter') {
      this.searchByDeviceId(deviceId);
    } else if (filterType === 'daysInactiveFilter') {
      this.searchByDaysInactiveFilter(daysInactive);
    }
  }

  public searchByAssetId(assetId: string) {
    if (!assetId) {
      return;
    }
    this.loader = true;
    this.liveDevicesListDataSource.data = new Array(1).fill(undefined);
    this.searchedAssetId = assetId;
    if (this.searchedDeviceId || this.searchedDaysInactiveRange) {
      this.searchedDeviceId = null;
      this.searchedDaysInactiveRange = null;
    }
    this.getcameraOnlineList();
  }

  public searchByDaysInactiveFilter(daysRange: string) {
    if (!daysRange) {
      return;
    }
    this.loader = true;
    this.liveDevicesListDataSource.data = new Array(1).fill(undefined);
    this.searchedDaysInactiveRange = daysRange;
    if (this.searchedDeviceId || this.searchedAssetId) {
      this.searchedDeviceId = null;
      this.searchedAssetId = null;
    }
    this.getcameraOnlineList();
  }

  updateSearchButtonDisabled(): void {
    const assetId = this.assetFilterForm.get('asset').value?.assetId;
    const deviceId = this.assetFilterForm.get('deviceId').value;
    const daysRange = this.assetFilterForm.get('daysInactive').value;

    this.isSearchButtonDisabled = !(assetId || deviceId || daysRange);
  }

  public searchByDeviceId(deviceId: string) {
    if (!deviceId) {
      return;
    }
    this.loader = true;
    this.liveDevicesListDataSource.data = new Array(1).fill(undefined);
    this.searchedDeviceId = deviceId;
    if (this.searchedAssetId || this.searchedDaysInactiveRange) {
      this.searchedAssetId = null;
      this.searchedDaysInactiveRange = null;
    }
    this.getcameraOnlineList();
  }

  public assetChanged(asset: AssetAutoCompleteResponseObject) {
    this.assetFilterForm.patchValue({ asset });
    this.updateSearchButtonDisabled();
  }

  private clearAssetId() {
    this.assetAutocomplete.clearInput();
  }

  private clearDeviceId() {
    this.assetFilterForm.patchValue({
      deviceId: '',
    });
  }

  private clearDaysInactive() {
    this.assetFilterForm.patchValue({
      daysInactive: '',
    });
  }

  public onPageChange(event) {
    this.offset = event.pageIndex * this.pageSize;
    this.getcameraOnlineList();
  }

  public getcameraOnlineList(isRefresh?: boolean) {
    const params: CamerOnlineStatusParams = {
      limit: this.pageSize,
      offset: this.offset,
      startDate: this.assetService.getStartDate(),
    };

    if (this.searchedAssetId) {
      params.assetId = this.searchedAssetId;
    }

    if (this.searchedDeviceId) {
      params.deviceId = this.searchedDeviceId;
    }

    if (this.searchedDaysInactiveRange) {
      params.daysInactive = this.searchedDaysInactiveRange;
    }

    if (this.sort && this.sort.active && this.sort.direction) {
      params.sortBy = this.sort.active; // Set sortBy parameter
      params.sort = this.sort.direction; // Set sortOrder parameter
    }

    this.loader = true;
    this.assetService
      .getCameraOnlineStatusEventList(isRefresh, params)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.loader = false;
        })
      )
      .subscribe(
        (res) => {
          this.liveDevicesListDataSource.data = res.rows;
          this.cameraOnlineStatustotalCount = res.totalCount;
        },
        () => {
          this.liveDevicesListDataSource.data = [];
          this.cameraOnlineStatustotalCount = 0;
        }
      );
  }

  public exportCameraStatus() {
    this.csvDownloadSuccess = null;
    this.csvDownloadLoader = true;

    const { filterType, asset, deviceId, daysInactive } = this.assetFilterForm.value || {};

    const body: CameraOnlineStatusDownloadReportBody = {
      reportType: 'DEVICE_PING',
      fleetPortal: true,
      startDate: this.assetService.getStartDate(),
    };
    if (filterType === 'assetIdFilter' && asset?.assetId) {
      body['assetId'] = asset?.assetId;
    } else if (filterType === 'deviceIdFilter' && deviceId) {
      body['deviceId'] = deviceId;
    } else if (filterType === 'daysInactiveFilter' && daysInactive) {
      body['daysInactive'] = daysInactive;
    }
    if (this.sort && this.sort.active && this.sort.direction) {
      body['sortBy'] = this.sort.active;
      body['sort'] = this.sort.direction;
    }
    this.mixpanelService.track('Health Check Table Download Reports', {
      'Report Type': 'DEVICE_PING',
      'Start Date': this.assetService.getStartDate(),
    });

    this.asyncDownloadsService
      .requestDownloadReport(body)
      .pipe(
        finalize(() => (this.csvDownloadLoader = false)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.csvDownloadSuccess = true;
        },
        (err: HttpErrorResponse) => {
          this.csvDownloadSuccess = false;
          if (err?.status === 409) {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  public navigateToDownloads() {
    this.asyncDownloadsService.navigateToExportHistory(AsyncReportType.devicePing.key);
  }

  public schedule() {
    this.mixpanelService.track('Schedule Clicked- From Device Online Status Export option', {
      'Report Type': ScheduledReportType.fleetdevicePing.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(ScheduledReportType.fleetdevicePing.key);
  }
}
